import React, { useContext, useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import NotificationItem from "examples/Items/NotificationItem";
import deadlines from "layouts/dashboard/components/Projects/data/deadlines";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import { UserContext } from "context";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const { user } = useContext(UserContext);
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const fetchedDeadlines = deadlines(0);
  const fetchedAdminDeadlines = user.isAdmin
    ? deadlines({
        mode: 0,
        admin: user.isAdmin,
      })
    : { rows: [] };
  const navigate = useNavigate();

  // Track which notifications have been read (for immediate UI removal)
  const [readNotificationIds, setReadNotificationIds] = useState(new Set());

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Mark notification as read
  const markNotificationAsRead = async (docId, isDocument = true) => {
    try {
      // Mark as read immediately in UI
      setReadNotificationIds(prev => new Set(prev).add(docId));

      // Call backend to persist the change
      await fetch(`${process.env.REACT_APP_AUTH_API_URL}/notification/mark_read.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: isDocument ? "document" : "event",
          id: docId,
          isAdmin: user.isAdmin || false,
        }),
      });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Render the notifications menu
  const renderMenu = () => {
    // Filter out notifications that have been marked as read AND have valid IDs
    const visibleUserNotifications = (fetchedDeadlines.rows || []).filter(
      n => !readNotificationIds.has(n.id) && n.event_id && n.permission_id
    );
    const visibleAdminNotifications = (fetchedAdminDeadlines.rows || []).filter(
      n => !readNotificationIds.has(n.id) && n.id
    );

    return (
      <Menu
        anchorEl={openMenu}
        anchorReference={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(openMenu)}
        onClose={handleCloseMenu}
        sx={{ mt: 2 }}
      >
        {visibleUserNotifications.length === 0 && visibleAdminNotifications.length === 0 ? (
          <NotificationItem icon={<Icon>info</Icon>} title="Nessun documento in sospeso" />
        ) : (
          <>
            {visibleUserNotifications.map((deadline, index) => (
              <NotificationItem
                key={`user-${index}`}
                icon={<Icon>warning</Icon>}
                title={`C'è un documento che richiede la tua attenzione per ${deadline.event_name}`}
                dateTime={`Scadenza: ${deadline.deadline}`}
                onClick={() => {
                  handleCloseMenu();
                  markNotificationAsRead(deadline.id, true);
                  navigate(`/app/event/${deadline.event_id}/permission/${deadline.permission_id}`);
                }}
              />
            ))}

            {user.isAdmin && visibleAdminNotifications.map((deadline, index) => (
              <NotificationItem
                key={`admin-${index}`}
                icon={<Icon>warning</Icon>}
                title={`C'è una nuova richiesta evento ${deadline.event_name}`}
                dateTime={`Scadenza: ${deadline.deadline}`}
                onClick={() => {
                  handleCloseMenu();
                  markNotificationAsRead(deadline.id, false);
                  navigate(`/app/event/${deadline.id}`);
                }}
              />
            ))}
          </>
        )}
      </Menu>
    );
  };

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        ></MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <Link to="/app/profile">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge
                  badgeContent={
                    (fetchedDeadlines.rows || []).filter(n => !readNotificationIds.has(n.id)).length +
                    (fetchedAdminDeadlines.rows || []).filter(n => !readNotificationIds.has(n.id)).length
                  }
                  color="error"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <Icon sx={iconsStyle}>notifications</Icon>
                </Badge>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;

